// src/PrivacyPolicy.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "./contexts/CartContext";
import { useTheme } from "./contexts/ThemeContext";
import { ShoppingCart } from "lucide-react";
import logo from "./assets/logo.png";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { darkMode, setDarkMode } = useTheme();

  return (
    <div className={darkMode ? "dark" : ""}>
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 dark:from-gray-900 dark:to-gray-800 text-gray-800 dark:text-gray-200 font-sans">

        {/* HEADER — SAME AS PRODUCTS PAGE */}
        <header className="w-full py-4 shadow-sm bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-6xl mx-auto flex items-center justify-between px-4">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img src={logo} alt="Bubble Butter Logo" className="h-14 w-auto object-cover rounded-lg shadow-sm" />
              <h1 className="text-2xl font-bold text-pink-600 dark:text-pink-300">Bubble Butter</h1>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate("/")} className="hover:text-pink-600 dark:hover:text-pink-300 font-semibold">
                Home
              </button>
              <button onClick={() => navigate("/products")} className="hover:text-pink-600 dark:hover:text-pink-300 font-semibold">
                Products
              </button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition shadow-sm"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>
            </div>

            {/* Mobile Menu */}
            <div className="md:hidden flex items-center gap-4">
              {/* Home Icon */}
              <button
                onClick={() => navigate("/")}
                className="h-10 w-10 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 shadow-md hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-6 h-6 text-pink-600 dark:text-pink-300"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-10 0h10"
                  />
                </svg>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                {darkMode ? "🌙" : "☀️"}
              </button>
            </div>
          </div>
        </header>

        {/* CONTENT */}

        <main className="max-w-4xl mx-auto px-4 py-10">
          <h2 className="text-3xl font-bold mb-6 text-pink-600 dark:text-pink-300">
            Privacy Policy
          </h2>

          <p className="mb-4 text-sm opacity-80">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <section className="space-y-6 leading-relaxed">
            <p>
              Bubble Butter values your privacy. This Privacy Policy explains how
              we collect, use, and protect your information when you visit or
              make a purchase from our website.
            </p>

            <div>
              <h3 className="text-xl font-semibold mb-2">Information We Collect</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Name, phone number, email address</li>
                <li>Shipping and billing address</li>
                <li>Order details and purchase history</li>
                <li>Basic device and browser information</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-2">How We Use Your Information</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>To process and deliver your orders</li>
                <li>To communicate order updates and support responses</li>
                <li>To improve our products and website experience</li>
                <li>To comply with legal obligations</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-2">Data Protection</h3>
              <p>
                We implement reasonable security measures to protect your
                personal data. However, no method of transmission over the
                internet is 100% secure.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-2">Third-Party Services</h3>
              <p>
                We may use trusted third-party services (such as payment or
                delivery providers) strictly for order fulfillment. We do not
                sell or rent your personal information.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-2">Your Rights</h3>
              <p>
                You may request access, correction, or deletion of your personal
                data by contacting us.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-2">Contact Us</h3>
              <p>
                For any privacy-related questions, contact us at: <br />
                <strong>📧 bubblebutter.help@gmail.com</strong>
              </p>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
